import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { GlobalStyles } from "./styles/GlobalStyles";
import Navigation from "./components/Navigation";
import Hero from "./components/Hero";
import About from "./components/About";
import Experience from "./components/Experience";
import Contact from "./components/Contact";
import Projects from "./components/Projects";
import Library from "./components/Library";
import Hobbies from "./components/Hobbies";
import NotFound from "./components/NotFound";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { ThemeProvider } from "./context/ThemeContext";
import { useTheme } from "./context/ThemeContext";
import { lightTheme, darkTheme } from "./styles/theme";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import PageTrackingWrapper from "./components/PageTrackingWrapper";

function ResumeRedirect({ target }: { target: string }) {
  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return null;
}

const MainContainer = styled.div<{ theme: "light" | "dark" }>`
  background-color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  min-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  width: 100%;
  max-width: 100vw;

  @media (min-width: 768px) {
    height: 100vh;
    overflow-y: scroll;
    scroll-snap-type: y mandatory;
    scroll-behavior: smooth;

    /* Hide scrollbar on desktop */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  & > section {
    min-height: 100vh;
    overflow-x: hidden;
    overflow-y: hidden;
    position: relative;
    width: 100%;
    max-width: 100vw;

    @media (min-width: 768px) {
      scroll-snap-align: start;
      scroll-snap-stop: normal;
      min-height: 100vh;
      height: auto;
      overflow-x: hidden;
      overflow-y: visible;
    }
  }
`;

function AppContent() {
  const { theme } = useTheme();
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const isScrolling = useRef(false);
  const isMobile = useRef(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      isMobile.current = window.innerWidth < 768;
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile.current) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        if (isScrolling.current) return;
        const currentSection = document
          .elementFromPoint(window.innerWidth / 2, window.innerHeight / 2)
          ?.closest("section");
        if (currentSection) {
          const sections = Array.from(document.querySelectorAll("section"));
          const currentIndex = sections.indexOf(currentSection);
          if (e.key === "ArrowUp" && currentIndex > 0) {
            isScrolling.current = true;
            sections[currentIndex - 1].scrollIntoView({ behavior: "smooth" });
            setTimeout(() => {
              isScrolling.current = false;
            }, 1000);
          } else if (
            e.key === "ArrowDown" &&
            currentIndex < sections.length - 1
          ) {
            isScrolling.current = true;
            sections[currentIndex + 1].scrollIntoView({ behavior: "smooth" });
            setTimeout(() => {
              isScrolling.current = false;
            }, 1000);
          }
        }
      }
    };
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        if (isScrolling.current) return;
        const currentSection = document
          .elementFromPoint(window.innerWidth / 2, window.innerHeight / 2)
          ?.closest("section");
        if (currentSection) {
          const sections = Array.from(document.querySelectorAll("section"));
          const currentIndex = sections.indexOf(currentSection);
          if (e.deltaY > 0 && currentIndex < sections.length - 1) {
            isScrolling.current = true;
            sections[currentIndex + 1].scrollIntoView({ behavior: "smooth" });
            scrollTimeout.current = setTimeout(() => {
              isScrolling.current = false;
            }, 1000);
          } else if (e.deltaY < 0 && currentIndex > 0) {
            isScrolling.current = true;
            sections[currentIndex - 1].scrollIntoView({ behavior: "smooth" });
            scrollTimeout.current = setTimeout(() => {
              isScrolling.current = false;
            }, 1000);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("wheel", handleWheel);
      if (scrollTimeout.current !== null) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  return (
    <PageTrackingWrapper>
      <MainContainer theme={theme}>
        <GlobalStyles />
        <Navigation />
        <Hero />
        <About />
        <Experience />
        <Projects />
        <Hobbies />
        <Contact />
      </MainContainer>
    </PageTrackingWrapper>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route
            path="/resume/company"
            element={<ResumeRedirect target="/Thuan-Trinh-Van-Resume.pdf" />}
          />
          <Route
            path="/resume/personal"
            element={<ResumeRedirect target="/Leo-Resume.pdf" />}
          />
          <Route path="/library" element={<Library />} />
          <Route path="/nhathuocminhthuy/policy" element={<PrivacyPolicy />} />
          <Route path="/" element={<AppContent />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
